// src/components/ProyectoDialog.tsx
import { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useProyectosMeta,
  useProyectoMutations,
  type ProyectoRol,
  type ProyectoBillingMode,
} from '../hooks/useProyectos';

type Props = {
  open: boolean;
  initial?: any;
  onClose: () => void;
  onSaved: () => void;
};

type FormValues = {
  codigo: string;
  nombre: string;
  descripcion: string;
  rol: ProyectoRol;
  billingMode: ProyectoBillingMode;
  includeInReports: boolean;
  countsInTeachingHours: boolean;
  countsInStudentReports: boolean;
  isExamFlag: boolean;
  reportGroup: string;
  calendarColor: string;
  activo: boolean;
};

/* Paleta rápida (ajústala a tus colores de marca) */
const QUICK_COLORS = [
  '#1e88e5',
  '#43a047',
  '#f4511e',
  '#8e24aa',
  '#3949ab',
  '#fb8c00',
  '#e53935',
  '#00acc1',
  '#5e35b1',
  '#607d8b',
  '#b39ddb',
];

export default function ProyectoDialog({ open, initial, onClose, onSaved }: Props) {
  const { t } = useTranslation<'translation'>();
  const { data: meta } = useProyectosMeta();
  const { createProyecto, updateProyecto } = useProyectoMutations();

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      codigo: '',
      nombre: '',
      descripcion: '',
      rol: 'DEFAULT',
      billingMode: 'FACTURABLE',
      includeInReports: true,
      countsInTeachingHours: true,
      countsInStudentReports: true,
      isExamFlag: false,
      reportGroup: '',
      /* Para <input type="color"> conviene un valor por defecto válido */
      calendarColor: '#b39ddb',
      activo: true,
    },
  });

  useEffect(() => {
    reset({
      codigo: initial?.codigo ?? '',
      nombre: initial?.nombre ?? '',
      descripcion: initial?.descripcion ?? '',
      rol: (initial?.rol as ProyectoRol) ?? 'DEFAULT',
      billingMode: (initial?.billingMode as ProyectoBillingMode) ?? 'FACTURABLE',
      includeInReports: initial?.includeInReports ?? true,
      countsInTeachingHours: initial?.countsInTeachingHours ?? true,
      countsInStudentReports: initial?.countsInStudentReports ?? true,
      isExamFlag: initial?.isExamFlag ?? false,
      reportGroup: initial?.reportGroup ?? '',
      calendarColor: initial?.calendarColor ?? '#b39ddb',
      activo: initial?.activo ?? true,
    });
  }, [initial, reset]);

  const onSubmit = async (data: FormValues) => {
    const payload = {
      ...data,
      descripcion: data.descripcion?.trim() || null,
      reportGroup: data.reportGroup?.trim() || null,
      calendarColor: data.calendarColor?.trim() || null,
    };
    if (initial?.id) {
      await updateProyecto.mutateAsync({ id: initial.id, data: payload });
    } else {
      await createProyecto.mutateAsync(payload);
    }
    onSaved();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initial ? t('proyectoDialog.title.edit') : t('proyectoDialog.title.new')}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={t('proyectoDialog.field.codigo')}
              size="small"
              {...register('codigo')}
              disabled={!!initial}
            />
            <TextField
              label={t('proyectoDialog.field.nombre')}
              size="small"
              {...register('nombre')}
            />
            <TextField
              label={t('proyectoDialog.field.descripcion')}
              size="small"
              {...register('descripcion')}
              multiline
              minRows={2}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {/* Rol */}
              <FormControl size="small" fullWidth>
                <InputLabel>{t('proyectoDialog.field.rol')}</InputLabel>
                <Controller
                  control={control}
                  name="rol"
                  render={({ field }) => (
                    <Select {...field} label={t('proyectoDialog.field.rol')}>
                      {(meta?.roles ?? []).map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              {/* Billing */}
              <FormControl size="small" fullWidth>
                <InputLabel>{t('proyectoDialog.field.billing')}</InputLabel>
                <Controller
                  control={control}
                  name="billingMode"
                  render={({ field }) => (
                    <Select {...field} label={t('proyectoDialog.field.billing')}>
                      {(meta?.billingModes ?? []).map((b) => (
                        <MenuItem key={b} value={b}>
                          {b}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                label={t('proyectoDialog.field.grupoReporte')}
                size="small"
                {...register('reportGroup')}
              />

              {/* === Selector de color + swatches ===================== */}
              <Controller
                name="calendarColor"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('proyectoDialog.field.colorCalendario')}
                    type="color"
                    size="small"
                    value={field.value || '#b39ddb'}
                    onChange={(e) => field.onChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 170 }}
                  />
                )}
              />
            </Stack>

            {/* Swatches de colores rápidos */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              {QUICK_COLORS.map((c) => (
                <Tooltip title={c} key={c}>
                  <IconButton
                    size="small"
                    aria-label={t('proyectoDialog.field.elegirColor', { color: c }) as string}
                    onClick={() => setValue('calendarColor', c, { shouldDirty: true })}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: c,
                      border: (t) => `1px solid ${t.palette.divider}`,
                      '&:hover': { opacity: 0.9 },
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
            {/* ======================================================= */}

            {/* Toggles */}
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name="includeInReports"
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label={t('proyectoDialog.field.incluirInformes')}
              />
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name="countsInTeachingHours"
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label={t('proyectoDialog.field.cuentaDocencia')}
              />
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name="countsInStudentReports"
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label={t('proyectoDialog.field.cuentaAlumno')}
              />
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name="isExamFlag"
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label={t('proyectoDialog.field.examen')}
              />
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name="activo"
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label={t('proyectoDialog.field.activo')}
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
